import React, { useState, useEffect, useMemo } from "react";
import { Input, Select, message, Button, InputNumber } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { svg } from "../../../../../../Assets/svg";
import { useAppDispatch } from "../../../../../../store";
import { getUniqueBeamLengthsAPI } from "../../../quotationApis";

interface APRFormProps {
  initialData?: any;
  isEdit: boolean;
  index: number;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const generateRange = (
  min: number,
  max: number,
  step: number,
  unit: string = "",
) => {
  const options = [];
  for (let i = min; i <= max; i += step) {
    options.push({ label: `${i}${unit}`, value: i });
  }
  return options;
};

const ASSUMED_PROFILE_WIDTHS = [
  { label: "82mm", value: 82 },
  { label: "100mm", value: 100 },
  { label: "120mm", value: 120 },
];

const RUN_TYPES = [
  { label: "Single Run", value: "single-run" },
  { label: "Back-To-Back Run", value: "back-to-back-run" },
];

const APRForm: React.FC<APRFormProps> = ({
  initialData,
  isEdit,
  index,
  onSave,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const [uniqueBeamLengths, setUniqueBeamLengths] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [payload, setPayload] = useState<any>(() => {
    const initial = initialData || { bay_types: [] };
    // Ensure existing bay types have a unique ID for stable React rendering keys
    if (initial.bay_types && Array.isArray(initial.bay_types)) {
      initial.bay_types = initial.bay_types.map((bay: any) => ({
        ...bay,
        _id: bay._id || uuidv4(),
      }));
    }
    return initial;
  });

  const handleChange = (key: string, value: any) => {
    setPayload((prev: any) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const newErr = { ...prev };
      delete newErr[key];
      return newErr;
    });
  };

  const handleBayTypeChange = (bayIndex: number, key: string, value: any) => {
    const newBayTypes = [...(payload.bay_types || [])];
    if (!newBayTypes[bayIndex]) newBayTypes[bayIndex] = {};
    newBayTypes[bayIndex][key] = value;
    setPayload((prev: any) => ({ ...prev, bay_types: newBayTypes }));
  };

  // Sync bay types array length with `number_of_different_bay_types`
  useEffect(() => {
    const num = parseInt(payload.number_of_different_bay_types) || 0;
    setPayload((prev: any) => {
      const currentBays = prev.bay_types || [];
      if (currentBays.length === num) return prev;

      const newBays = [...currentBays];
      if (newBays.length < num) {
        // Add new bays with unique IDs
        while (newBays.length < num) {
          newBays.push({ _id: uuidv4() });
        }
      } else {
        // Remove extra bays
        newBays.splice(num);
      }
      return { ...prev, bay_types: newBays };
    });
  }, [payload.number_of_different_bay_types]);

  // Calculate Total Run Length
  const totalRunLength = useMemo(() => {
    if (
      !payload.bay_types ||
      !payload.assumed_profile_width ||
      !payload.number_of_bays_long
    )
      return 0;

    let baysTotal = 0;
    payload.bay_types.forEach((bay: any) => {
      baysTotal += (bay.beam_length || 0) * (bay.qty_of_bays || 0);
    });

    const profileTotal =
      payload.assumed_profile_width *
      (parseInt(payload.number_of_bays_long) + 1);
    return baysTotal + profileTotal;
  }, [
    payload.bay_types,
    payload.assumed_profile_width,
    payload.number_of_bays_long,
  ]);

  const getUniqueBeamLengths = () => {
    dispatch(
      getUniqueBeamLengthsAPI({
        onSuccess: (data) => {
          setUniqueBeamLengths(data || []);
        },
        onError: () => {
          setUniqueBeamLengths([]);
        },
      }),
    );
  };

  useEffect(() => {
    getUniqueBeamLengths();
  }, [dispatch]);

  // Update total run length in payload so it saves to backend
  useEffect(() => {
    if (payload.total_run_length !== totalRunLength) {
      setPayload((prev: any) => ({
        ...prev,
        total_run_length: totalRunLength,
      }));
    }
  }, [totalRunLength, payload.total_run_length]);

  const handleSaveClick = () => {
    const newErrors: Record<string, string> = {};

    if (!payload.number_of_runs) {
      newErrors.number_of_runs = "Number of runs is required";
    }
    if (!payload.run_type) {
      newErrors.run_type = "Run type is required";
    }
    if (!payload.number_of_bays_long) {
      newErrors.number_of_bays_long = "Number of bays long is required";
    }

    const totalAllocatedBays = (payload.bay_types || []).reduce(
      (sum: number, bay: any) => sum + (bay.qty_of_bays || 0),
      0,
    );

    if (
      payload.number_of_bays_long &&
      totalAllocatedBays !== parseInt(payload.number_of_bays_long)
    ) {
      message.error(
        `Total quantity of bays (${totalAllocatedBays}) must equal Number of bays long (${payload.number_of_bays_long})`,
      );
      return;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    onSave(payload);
  };

  const getRunDepthOptions = () => {
    if (payload.run_type === "single-run")
      return generateRange(400, 1600, 50, "mm");
    if (payload.run_type === "back-to-back-run")
      return generateRange(950, 4800, 50, "mm");
    return [];
  };

  const getAvailableQtyOptions = (currentIndex: number) => {
    const totalAllowed = parseInt(payload.number_of_bays_long) || 0;

    // If the user hasn't entered a total number of bays yet, they shouldn't
    // be able to select any quantity.
    if (totalAllowed === 0) return [];

    // Sum of all OTHER bays.
    // CRUCIAL UX LOGIC: If another bay hasn't been assigned a quantity yet,
    // we MUST reserve at least 1 bay for it. This guarantees that no section
    // is ever starved and left with 0 available options.
    const alreadyAllocated = (payload.bay_types || []).reduce(
      (sum: number, bay: any, idx: number) => {
        if (idx === currentIndex) return sum;
        // If it has a value, use it. If it's empty/undefined, reserve 1.
        const effectiveQty = bay.qty_of_bays ? parseInt(bay.qty_of_bays) : 1;
        return sum + effectiveQty;
      },
      0,
    );

    // The maximum this current bay can be is whatever is left over after reservations
    const maxOptions = totalAllowed - alreadyAllocated;

    return generateRange(1, Math.max(1, maxOptions), 1, "");
  };

  return (
    <div className="row align-items-center gy-sm-3 gy-2">
      <div className="text-black fs-5 col-sm-6 order-sm-1 order-1">
        {isEdit ? "Edit" : "Add"} Run Type {index}
      </div>
      <div className="col-sm-6 order-sm-2 order-3">
        <div className="row justify-content-end">
          <div className="col-sm-auto col-6">
            <button
              className="btn btn-outline-secondary btn-lg px-3 fw-bold w-100"
              onClick={onCancel}
            >
              CANCEL
            </button>
          </div>
          <div className="col-sm-auto col-6">
            <button
              className="btn btn-primary btn-lg px-4 w-100"
              style={{ backgroundColor: "#212529" }}
              onClick={handleSaveClick}
            >
              SAVE
            </button>
          </div>
        </div>
      </div>

      <div className="col-12 order-sm-3 order-2">
        <div className="border-top pt-3">
          <div className="row g-md-4 g-3 mb-4">
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">Number of runs</label>
                <Input
                  type="number"
                  className="form-control form-control-lg"
                  placeholder="0"
                  value={payload.number_of_runs || ""}
                  onChange={(e) =>
                    handleChange("number_of_runs", e.target.value)
                  }
                  onWheel={(e: any) => e.currentTarget.blur()}
                />
                {errors.number_of_runs && (
                  <div className="text-danger small mt-1">
                    {errors.number_of_runs}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">Run type</label>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={RUN_TYPES}
                  placeholder="Select Run type"
                  className="w-100"
                  suffixIcon={svg?.selectDownArrow}
                  value={payload.run_type}
                  onChange={(val) => handleChange("run_type", val)}
                />
                {errors.run_type && (
                  <div className="text-danger small mt-1">
                    {errors.run_type}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">
                  Number of bays long
                </label>
                <Input
                  type="number"
                  className="form-control form-control-lg"
                  placeholder="1"
                  min={1}
                  value={payload.number_of_bays_long || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      handleChange("number_of_bays_long", val);
                    } else if (parseInt(val) > 0) {
                      handleChange("number_of_bays_long", val);
                    }
                  }}
                  onWheel={(e: any) => e.currentTarget.blur()}
                />
                {errors.number_of_bays_long && (
                  <div className="text-danger small mt-1">
                    {errors.number_of_bays_long}
                  </div>
                )}
              </div>
            </div>
            {payload.run_type === "back-to-back-run" && (
              <div className="col-lg-3 col-md-4 col-sm-6">
                <div className="form-item">
                  <label className="custom-label mb-1 ff">
                    Total number of bays in the run
                  </label>
                  <div className="form-control form-control-lg bg-light d-flex align-items-center">
                    {(parseInt(payload.number_of_bays_long) || 0) * 2}
                  </div>
                </div>
              </div>
            )}
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">
                  Assumed profile width
                </label>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={ASSUMED_PROFILE_WIDTHS}
                  placeholder="Select profile width"
                  className="w-100"
                  suffixIcon={svg?.selectDownArrow}
                  value={payload.assumed_profile_width}
                  onChange={(val) => handleChange("assumed_profile_width", val)}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">
                  Number of different bay types
                </label>
                <div className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput">
                  <Button
                    type="text"
                    icon={<MinusOutlined />}
                    className="btn btn-primary addRemoveBtn"
                    onClick={() => {
                      const currentVal =
                        parseInt(payload.number_of_different_bay_types) || 0;
                      handleChange(
                        "number_of_different_bay_types",
                        Math.max(0, currentVal - 1),
                      );
                    }}
                  />
                  <InputNumber
                    min={0}
                    readOnly
                    onWheel={(e) => e.currentTarget.blur()}
                    value={parseInt(payload.number_of_different_bay_types) || 0}
                    onChange={(val) =>
                      handleChange("number_of_different_bay_types", val || 0)
                    }
                    className="border-0 text-center w-100 shadow-none"
                    controls={false}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    className="btn btn-primary addRemoveBtn"
                    onClick={() => {
                      const currentVal =
                        parseInt(payload.number_of_different_bay_types) || 0;
                      handleChange(
                        "number_of_different_bay_types",
                        currentVal + 1,
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bay Types Dynamic Blocks */}
          {payload.bay_types && payload.bay_types.length > 0 && (
            <div className="row g-md-4 g-3 mb-4">
              {payload.bay_types.map((bay: any, idx: number) => (
                <div key={bay._id} className="col-12">
                  <div className="p-3 border rounded-3 bg-light bg-opacity-50">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 text-secondary ff fw-bold">
                        Bay type {idx + 1}
                      </h6>
                      <button
                        type="button"
                        className="btn btn-link p-0 svg-32 text-danger"
                        onClick={() => {
                          const newBayTypes = [...payload.bay_types];
                          newBayTypes.splice(idx, 1);
                          const currentNum =
                            parseInt(payload.number_of_different_bay_types) ||
                            0;
                          setPayload((prev: any) => ({
                            ...prev,
                            bay_types: newBayTypes,
                            number_of_different_bay_types: Math.max(
                              0,
                              currentNum - 1,
                            ),
                          }));
                        }}
                      >
                        {svg?.deleteBtn}
                      </button>
                    </div>
                    <div className="row g-3">
                      <div className="col-lg-4 col-md-6">
                        <div className="form-item">
                          <label className="custom-label mb-1 ff">
                            Beam length
                          </label>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={uniqueBeamLengths?.map((beam: any) => ({
                              value: beam.toString(),
                              label: `${beam}mm`,
                            }))}
                            placeholder="Select Beam length"
                            className="w-100"
                            suffixIcon={svg?.selectDownArrow}
                            value={bay.beam_length}
                            onChange={(val) =>
                              handleBayTypeChange(idx, "beam_length", val)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="form-item">
                          <label className="custom-label mb-1 ff">
                            Beam levels (G+)
                          </label>
                          <Input
                            type="number"
                            className="form-control form-control-lg"
                            placeholder="0"
                            value={bay.beam_levels || ""}
                            onChange={(e) =>
                              handleBayTypeChange(
                                idx,
                                "beam_levels",
                                e.target.value,
                              )
                            }
                            onWheel={(e: any) => e.currentTarget.blur()}
                          />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-12">
                        <div className="form-item">
                          <label className="custom-label mb-1 ff">
                            {payload.run_type === "back-to-back-run"
                              ? "Qty of bays (each side of the back-to-back run)"
                              : "Qty of bays"}
                          </label>
                          <Select
                            showSearch
                            optionFilterProp="label"
                            allowClear
                            options={getAvailableQtyOptions(idx)}
                            placeholder="Select Qty"
                            className="w-100"
                            suffixIcon={svg?.selectDownArrow}
                            value={bay.qty_of_bays}
                            onChange={(val) =>
                              handleBayTypeChange(idx, "qty_of_bays", val)
                            }
                            disabled={!payload.number_of_bays_long}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="row g-md-4 g-3 mb-4">
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">Total run height</label>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={generateRange(800, 18000, 100, "mm")}
                  placeholder="Select run height"
                  className="w-100"
                  suffixIcon={svg?.selectDownArrow}
                  value={payload.total_run_height}
                  onChange={(val) => handleChange("total_run_height", val)}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">Total run length</label>
                <div className="form-control form-control-lg bg-light d-flex align-items-center">
                  {totalRunLength ? `${totalRunLength}mm` : "-"}
                </div>
              </div>
            </div>

            <div className="col-lg-3 col-md-4 col-sm-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">Total run depth</label>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={getRunDepthOptions()}
                  placeholder="Select run depth"
                  className="w-100"
                  suffixIcon={svg?.selectDownArrow}
                  value={payload.total_run_depth}
                  onChange={(val) => handleChange("total_run_depth", val)}
                />
              </div>
            </div>
          </div>

          <div className="mt-2 mb-3  fw-bold fs-6 ">
            Racking to be spec'd to suit
          </div>
          <div className="row g-md-4 g-3">
            <div className="col-lg-4 col-md-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">
                  Max load per level (majority of levels)
                </label>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={generateRange(100, 7000, 50, "kg")}
                  placeholder="Select max load"
                  className="w-100"
                  suffixIcon={svg?.selectDownArrow}
                  value={payload.max_load_per_level}
                  onChange={(val) => handleChange("max_load_per_level", val)}
                />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="form-item">
                <label className="custom-label mb-1 ff">
                  Max load per bay (majority of bays)
                </label>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={generateRange(3000, 23200, 100, "kg")}
                  placeholder="Select max load"
                  className="w-100"
                  suffixIcon={svg?.selectDownArrow}
                  value={payload.max_load_per_bay}
                  onChange={(val) => handleChange("max_load_per_bay", val)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const APRView: React.FC<{ item: any }> = ({ item }) => {
  const isBackToBack = item.run_type === "back-to-back-run";

  return (
    <>
      <div className="row g-md-4 g-3 mb-4">
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label className="custom-label mb-0 ff text-949494">
            Number of runs
          </label>
          <div className="mb-0 fs-6 fw-semibold d-block ">
            {item.number_of_runs || "-"}
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label className="custom-label mb-0 ff text-949494">Run type</label>
          <div className="mb-0 fs-6 fw-semibold d-block text-capitalize">
            {typeof item.run_type === "string"
              ? RUN_TYPES.find((rt) => rt.value === item.run_type)?.label ||
                item.run_type
              : "-"}
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label className="custom-label mb-0 ff text-949494">
            Number of bays long
          </label>
          <div className="mb-0 fs-6 fw-semibold d-block ">
            {item.number_of_bays_long || "-"}
          </div>
        </div>
        {isBackToBack && (
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="custom-label mb-0 ff text-949494">
              Total number of bays in the run
            </label>
            <div className="mb-0 fs-6 fw-semibold d-block ">
              {(parseInt(item.number_of_bays_long) || 0) * 2}
            </div>
          </div>
        )}
      </div>

      <div className="row g-md-4 g-3 mb-4">
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label className="custom-label mb-0 ff text-949494">
            Total run height
          </label>
          <div className="mb-0 fs-6 fw-semibold d-block ">
            {item.total_run_height ? `${item.total_run_height}mm` : "-"}
          </div>
        </div>
        <div className="col-lg-6 col-md-8 col-sm-12">
          <label className="custom-label mb-0 ff text-949494">
            Total run length{" "}
            {item.assumed_profile_width
              ? `(assumed profile width of ${item.assumed_profile_width}mm)`
              : ""}
          </label>
          <div className="mb-2 fs-6 fw-semibold d-block ">
            {item.total_run_length ? `${item.total_run_length}mm` : "-"}
          </div>
          {item.bay_types && item.bay_types.length > 0 && (
            <div
              className="fs-6 ff"
              style={{ color: "var(--bs-body-color)", opacity: 0.85 }}
            >
              <div className="mb-1">
                Consisting of{" "}
                {isBackToBack ? "(each side of the back-to-back run)" : ""}
              </div>
              {item.bay_types.map((bay: any, idx: number) => {
                if (!bay.qty_of_bays) return null;
                const qty = parseInt(bay.qty_of_bays);
                return (
                  <div key={idx}>
                    - {qty} no. {bay.beam_length || 0}mm wide{" "}
                    {qty === 1 ? "bay" : "bays"} with {bay.beam_levels || 0}{" "}
                    beam levels
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label className="custom-label mb-0 ff text-949494">
            Total run depth
          </label>
          <div className="mb-0 fs-6 fw-semibold d-block ">
            {item.total_run_depth ? `${item.total_run_depth}mm` : "-"}
          </div>
        </div>
      </div>

      <div className="mb-3 fw-bold fs-6 mt-4">Racking to be spec'd to suit</div>
      <div className="row g-md-4 g-3">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <label className="custom-label mb-0 ff text-949494">
            Max load per level (majority of levels)
          </label>
          <div className="mb-0 fs-6 fw-semibold d-block ">
            {item.max_load_per_level ? `${item.max_load_per_level}kg` : "-"}
          </div>
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <label className="custom-label mb-0 ff text-949494">
            Max load per bay (majority of bays)
          </label>
          <div className="mb-0 fs-6 fw-semibold d-block ">
            {item.max_load_per_bay ? `${item.max_load_per_bay}kg` : "-"}
          </div>
        </div>
      </div>
    </>
  );
};

export default APRForm;
